import { Component, OnDestroy } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';

@Component({
  selector: 'pps-project-invoice-header',
  template: `
    <div
      class="pps-project-invoice-header"
      [class.is-sortable]="isSortable"
      [title]="headerTitle"
      (click)="onSortRequested($event)">
      <i *ngIf="params?.iconClass" [ngClass]="params.iconClass.split(' ')" aria-hidden="true"></i>
      <span class="header-label">{{ params?.displayName }}</span>
      <span *ngIf="isSortable" class="sort-indicator" aria-hidden="true">
        {{ sortDirection === 'asc' ? '▲' : sortDirection === 'desc' ? '▼' : '↕' }}
      </span>
      <span *ngIf="isFilterActive" class="filter-indicator" title="Filter applied" aria-hidden="true">●</span>
      <button
        *ngIf="canOpenMenu"
        type="button"
        class="menu-button"
        title="Open column menu"
        (click)="openMenu($event)">
        ☰
      </button>
    </div>
  `,
  styles: [`
    .pps-project-invoice-header {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      width: 100%;
      overflow: hidden;
    }
    .pps-project-invoice-header.is-sortable {
      cursor: pointer;
      user-select: none;
    }
    .pps-project-invoice-header i {
      font-style: normal;
      line-height: 1;
    }
    .header-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .sort-indicator {
      margin-left: auto;
      font-size: 0.75rem;
      opacity: 0.8;
    }
    .filter-indicator {
      color: #0a7d32;
      font-size: 0.65rem;
      margin-left: 0.15rem;
    }
    .menu-button {
      border: 0;
      background: transparent;
      line-height: 1;
      cursor: pointer;
      padding: 0 0.1rem;
      margin-left: 0.2rem;
      opacity: 0.75;
    }
    .menu-button:hover {
      opacity: 1;
    }
  `]
})
export class ProjectInvoiceHeaderComponent implements IHeaderAngularComp, OnDestroy {
  public params: any;
  public sortDirection: 'asc' | 'desc' | null = null;
  public isFilterActive = false;
  public isSortable = false;
  public canOpenMenu = false;

  private readonly onColumnStateChanged = () => {
    this.updateUiState();
  };

  get headerTitle(): string {
    const colDef = this.params?.column && typeof this.params.column.getColDef === 'function'
      ? this.params.column.getColDef()
      : null;

    const tooltip = colDef?.headerTooltip || this.params?.tooltip || this.params?.displayName;
    return tooltip || '';
  }

  agInit(params: IHeaderParams): void {
    this.params = params;
    this.bindColumnEvents();
    this.updateUiState();
  }

  refresh(params: IHeaderParams): boolean {
    this.unbindColumnEvents();
    this.params = params;
    this.bindColumnEvents();
    this.updateUiState();
    return true;
  }

  ngOnDestroy(): void {
    this.unbindColumnEvents();
  }

  onSortRequested(event: MouseEvent): void {
    if (!this.isSortable || typeof this.params?.progressSort !== 'function') {
      return;
    }
    this.params.progressSort(event.shiftKey);
  }

  openMenu(event: MouseEvent): void {
    event.stopPropagation();

    const anchor = event.currentTarget as HTMLElement;
    if (typeof this.params?.showColumnMenuAfterButtonClick === 'function') {
      this.params.showColumnMenuAfterButtonClick(anchor);
      return;
    }

    if (typeof this.params?.showColumnMenu === 'function') {
      this.params.showColumnMenu(anchor);
      return;
    }

    if (typeof this.params?.showFilter === 'function') {
      this.params.showFilter(anchor);
    }
  }

  private bindColumnEvents(): void {
    const column = this.params?.column;
    if (!column || typeof column.addEventListener !== 'function') {
      return;
    }

    column.addEventListener('sortChanged', this.onColumnStateChanged);
    column.addEventListener('filterChanged', this.onColumnStateChanged);
  }

  private unbindColumnEvents(): void {
    const column = this.params?.column;
    if (!column || typeof column.removeEventListener !== 'function') {
      return;
    }

    column.removeEventListener('sortChanged', this.onColumnStateChanged);
    column.removeEventListener('filterChanged', this.onColumnStateChanged);
  }

  private updateUiState(): void {
    const column = this.params?.column;
    this.sortDirection = column && typeof column.getSort === 'function' ? column.getSort() : null;
    this.isFilterActive = !!(column && typeof column.isFilterActive === 'function' && column.isFilterActive());
    this.isSortable = !!this.params?.enableSorting;
    this.canOpenMenu =
      typeof this.params?.showColumnMenuAfterButtonClick === 'function' ||
      typeof this.params?.showColumnMenu === 'function' ||
      typeof this.params?.showFilter === 'function';
  }
}
